import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { DropdownInfoConfig, DropdownOptionLike } from '../../../../core/constants/api-endpoints';
import { ExpenseService } from '../../../../core/services';
import { CommonDropdownComponent } from '../../../../core/ui/common-dropdown/common-dropdown.component';
import { TranslatePipe } from '../../../../core/pipes/translate.pipe';
import { LanguageService } from '../../../../core/services/language.service';

@Component({
  selector: 'app-new-edit-expense',
  standalone: true,
  imports: [CommonModule, FormsModule, CommonDropdownComponent, MatDatepickerModule, MatInputModule, MatNativeDateModule, MatSnackBarModule, TranslatePipe],
  templateUrl: './new-edit-expense.component.html',
  styleUrls: ['./new-edit-expense.component.css', './../../../../../CSSFiles/BTN_Style.css']
})
export class NewEditExpenseComponent implements OnInit {
  private readonly noFlatNeededValue = '__NO_FLAT_NEEDED__';
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() initialExpense: Record<string, any> | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  houseDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'houseId',
    apiInfo: 'HOUSE_LIST',
    status: 1,
    valueKey: 'id',
    display1: 'houseName',
    display2: 'houseNo',
    label: 'filter.house',
    placeholder: 'placeholder.selectHouse',
    isSearchable: true
  };

  flatDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'flatId',
    apiInfo: 'FLAT_LIST',
    status: 1,
    valueKey: 'id',
    display1: 'flatName',
    display2: 'floorName',
    label: 'filter.flat',
    placeholder: 'placeholder.selectFlat',
    isSearchable: true
  };

  flatDropdownWithNoFlatOption: Partial<DropdownInfoConfig> = {
    ...this.flatDropdownObj,
    hideLoadingMessage: true,
    extraOptions: [
      {
        id: this.noFlatNeededValue,
        flatName: 'House Expense',
        floorName: '',
        __pinToStart: true,
        __highlighted: true
      }
    ]
  };

  expenseHeadDropdownObj: Partial<DropdownInfoConfig> = {
    name: 'expenseHeadId',
    apiInfo: 'EXPENSE_HEAD_LIST',
    status: 1,
    valueKey: 'id',
    display1: 'name',
    label: 'filter.expenseHead',
    placeholder: 'placeholder.selectExpenseHead',
    isSearchable: true
  };

  flatDropdownFilters: Record<string, unknown> = {};

  expense = {
    id: null as number | null,
    houseId: null as number | null,
    flatId: null as number | null,
    expenseHeadId: null as number | null,
    remarks: '',
    expenseAmount: null as number | null,
    expenseDate: '',
    expenseMonth: null as number | null,
    expenseYear: null as number | null,
    status: 1
  };

  matDateValue: Date | null = null;
  selectedFlatValue: number | string | null = null;
  isSubmitting = false;
  error: string | null = null;

  constructor(
    private readonly snackBar: MatSnackBar,
    private readonly language: LanguageService,
    private readonly expenses: ExpenseService
  ) {}

  ngOnInit(): void {
    this.setDefaultDate();
    this.applyEditValues();
  }

  closeModal(): void {
    this.close.emit();
  }

  onHouseChange(event: { value: unknown }): void {
    const houseId = Number(event.value);
    const nextHouseId = Number.isFinite(houseId) ? houseId : null;
    const houseChanged = this.expense.houseId !== nextHouseId;
    this.expense.houseId = nextHouseId;
    this.flatDropdownFilters = this.expense.houseId ? { 'house.id': this.expense.houseId } : {};

    if (houseChanged) {
      this.expense.flatId = null;
      this.selectedFlatValue = this.isNoFlatNeededSelected ? this.noFlatNeededValue : null;
    }
  }

  onHouseOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    if (this.expense.houseId || event.options.length !== 1) {
      return;
    }

    const houseId = Number(event.options[0]?.['id']);
    if (!Number.isFinite(houseId)) {
      return;
    }

    this.expense.houseId = houseId;
    this.flatDropdownFilters = { 'house.id': houseId };
  }

  onFlatChange(event: { value: unknown; option?: Record<string, any> }): void {
    if (event.value === this.noFlatNeededValue) {
      this.selectedFlatValue = this.noFlatNeededValue;
      this.expense.flatId = null;
      return;
    }

    const flatId = Number(event.value);
    const nextFlatId = Number.isFinite(flatId) ? flatId : null;
    this.selectedFlatValue = nextFlatId;
    this.expense.flatId = nextFlatId;
    const flatHouseId = Number(event.option?.['house']?.['id']);
    if (nextFlatId && Number.isFinite(flatHouseId)) {
      this.expense.houseId = flatHouseId;
      this.flatDropdownFilters = { 'house.id': flatHouseId };
    }
  }

  onFlatOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    if (this.selectedFlatValue !== null || event.options.length !== 1) {
      return;
    }

    const onlyOption = event.options[0];
    if (onlyOption?.['id'] === this.noFlatNeededValue) {
      this.selectedFlatValue = this.noFlatNeededValue;
      this.expense.flatId = null;
      return;
    }

    const flatId = Number(onlyOption?.['id']);
    if (!Number.isFinite(flatId)) {
      return;
    }

    this.selectedFlatValue = flatId;
    this.expense.flatId = flatId;
  }

  onExpenseHeadChange(event: { value: unknown }): void {
    const expenseHeadId = Number(event.value);
    this.expense.expenseHeadId = Number.isFinite(expenseHeadId) ? expenseHeadId : null;
  }

  onExpenseHeadOptionsLoaded(event: { options: DropdownOptionLike[] }): void {
    if (this.expense.expenseHeadId || event.options.length !== 1) {
      return;
    }

    const expenseHeadId = Number(event.options[0]?.['id']);
    if (!Number.isFinite(expenseHeadId)) {
      return;
    }

    this.expense.expenseHeadId = expenseHeadId;
  }

  onDateSelected(event: MatDatepickerInputEvent<Date>): void {
    const pickedDate = event.value;
    if (!pickedDate || Number.isNaN(pickedDate.getTime())) {
      return;
    }
    this.setDateValues(pickedDate);
  }

  submitForm(): void {
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSubmitting = true;
    this.error = null;

    const request$ = this.mode === 'edit'
      ? this.expenses.update(payload)
      : this.expenses.create(payload);

    request$
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => this.save.emit(),
        error: (err: any) => {
          const message = err?.message || `Failed to ${this.mode === 'edit' ? 'update' : 'create'} expense.`;
          this.error = message;
          this.openErrorToast(message);
        }
      });
  }

  private setDefaultDate(): void {
    this.setDateValues(new Date());
  }

  private applyEditValues(): void {
    if (this.mode !== 'edit' || !this.initialExpense) {
      return;
    }

    const sourceFlat = this.initialExpense['flat'] as Record<string, any> | undefined;
    const sourceHouse = (this.initialExpense['house'] as Record<string, any> | undefined)
      ?? (sourceFlat?.['house'] as Record<string, any> | undefined);
    const sourceExpenseHead = this.initialExpense['expenseHead'] as Record<string, any> | undefined;
    const sourceDateText = this.toDisplayDateValue(this.initialExpense['expenseDate']);
    const parsedDate = this.toDateFromDisplay(sourceDateText);

    const houseId = Number(sourceHouse?.['id']);
    const flatId = Number(sourceFlat?.['id']);
    this.expense.houseId = Number.isFinite(houseId) ? houseId : null;
    this.expense.flatId = Number.isFinite(flatId) ? flatId : null;
    this.selectedFlatValue = this.expense.flatId
      ? this.expense.flatId
      : (this.expense.houseId ? this.noFlatNeededValue : null);
    this.flatDropdownFilters = this.expense.houseId ? { 'house.id': this.expense.houseId } : {};

    this.expense.id = Number(this.initialExpense['id']) || null;
    this.expense.expenseHeadId = Number(sourceExpenseHead?.['id']) || null;
    this.expense.remarks = `${this.initialExpense['remarks'] ?? ''}`;
    this.expense.expenseAmount = Number(this.initialExpense['expenseAmount'] ?? 0);
    if (parsedDate) {
      this.setDateValues(parsedDate);
    }
  }

  private setDateValues(date: Date): void {
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    this.expense.expenseDate = `${day}-${month}-${year}`;
    this.expense.expenseMonth = Number(month);
    this.expense.expenseYear = year;
    this.matDateValue = new Date(year, Number(month) - 1, Number(day));
  }

  private buildPayload(): Record<string, any> | null {
    if (!this.expense.houseId) {
      this.error = 'House is required.';
      return null;
    }

    if (!this.expense.flatId && !this.isNoFlatNeededSelected) {
      this.error = 'Flat is required.';
      return null;
    }

    if (!this.expense.expenseHeadId) {
      this.error = 'Expense head is required.';
      return null;
    }

    if (!this.expense.expenseDate) {
      this.error = 'Expense date is required.';
      return null;
    }

    if (!this.expense.expenseAmount || this.expense.expenseAmount <= 0) {
      this.error = 'Expense amount must be greater than 0.';
      return null;
    }

    const apiDate = this.toApiDateValue(this.expense.expenseDate);
    if (!apiDate) {
      this.error = 'Expense date must be valid.';
      return null;
    }

    const payload: Record<string, any> = {
      house: { id: this.expense.houseId },
      expenseHead: { id: this.expense.expenseHeadId },
      remarks: `${this.expense.remarks ?? ''}`.trim(),
      expenseAmount: Number(this.expense.expenseAmount || 0),
      expenseDate: apiDate,
      expenseMonth: this.expense.expenseMonth,
      expenseYear: this.expense.expenseYear,
      status: 1
    };

    if (this.expense.flatId) {
      payload['flat'] = { id: this.expense.flatId };
    }

    if (this.mode === 'edit') {
      if (!this.expense.id) {
        this.error = 'Expense id is missing for update.';
        return null;
      }
      payload['id'] = this.expense.id;
    }

    return payload;
  }

  private toDisplayDateValue(value: unknown): string {
    if (!value) {
      return '';
    }

    const text = `${value}`.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(text)) {
      return text;
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
      const [year, month, day] = text.split('-');
      return `${day}-${month}-${year}`;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }

    const day = `${parsed.getDate()}`.padStart(2, '0');
    const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private toDateFromDisplay(value: string): Date | null {
    const match = (value ?? '').trim().match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return parsed;
  }

  private toApiDateValue(value: string): string | null {
    const trimmed = `${value ?? ''}`.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const match = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!match) {
      return null;
    }
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const parsed = new Date(year, month - 1, day);
    if (
      parsed.getFullYear() !== year ||
      parsed.getMonth() !== month - 1 ||
      parsed.getDate() !== day
    ) {
      return null;
    }
    return `${year}-${`${month}`.padStart(2, '0')}-${`${day}`.padStart(2, '0')}`;
  }

  private openErrorToast(message: string): void {
    this.snackBar.open(message, this.language.translate('common.close', 'Close'), {
      duration: 3500,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['dashboard-toast-warning']
    });
  }

  get isNoFlatNeededSelected(): boolean {
    return this.selectedFlatValue === this.noFlatNeededValue;
  }
}
